import { type ReactElement, type ReactNode, useSyncExternalStore } from 'react'

import { buildDraggableProps, buildDroppableProps, type DndElementProps, handlerRef, stampDndProps } from './bindings'
import { DND_ZONE_ATTR, type DndEdge, type DndEngineHandlers, isDraggedPayload, registerDndKit, subscribeDraggedPayload } from './engine'

export type { DndEdge } from './engine'
export { DND_DRAG_HANDLE_ATTR } from './engine'

// `D` is the drop id type seen by handlers. It is `DropId` when the kit has its
// own onDrop (every droppable then needs an id to tell targets apart), and
// `DropId | undefined` when each droppable handles its own drops.

export type DndCanDropArgs<P, D extends string | undefined = string | undefined> = {
    payload: P
    dropId: D
    dropElement: HTMLElement
}

export type DndDropEvent<P, D extends string | undefined = string | undefined> = {
    payload: P
    dropId: D
    dropElement: HTMLElement
    /** Pointer half of the hovered target at release; null on the keyboard path and when dropping onto the dragged element itself. */
    edge: DndEdge | null
}

export type DndTargetChange<P, D extends string | undefined = string | undefined> = {
    payload: P
    /** The hovered target, or null once the drag leaves every target. */
    over: { dropId: D; dropElement: HTMLElement; valid: boolean; edge: DndEdge | null } | null
}

/** A drag hovering one droppable. Not hovering is reported as null, not as a field. */
export type DndHover<P> = {
    payload: P
    /** The canDrop verdict for this droppable: true when releasing here would drop. */
    canDrop: boolean
    /** Pointer half of this droppable; null when canDrop is false, over the dragged element itself, and on the keyboard path. */
    edge: DndEdge | null
}

type DropOptionsBase<P, D extends string | undefined> = {
    /** Overrides the zone and kit canDrop for this target. */
    canDrop?: (args: DndCanDropArgs<P, D>) => boolean
    /**
     * Hover state of this droppable only. Every call with a hover is followed by
     * a null call once the drag moves elsewhere, drops, or cancels, so it can
     * feed a useState setter directly.
     */
    onHoverChange?: (hover: DndHover<P> | null) => void
    disabled?: boolean
}

type LocalDropTarget<P, D extends string | undefined> = {
    id?: Exclude<D, undefined>
    onDrop: (event: DndDropEvent<P, D>) => void
}

type KitDropTarget<P, D extends string | undefined> = {
    id: Exclude<D, undefined>
    /** Overrides the kit onDrop for this target. */
    onDrop?: (event: DndDropEvent<P, D>) => void
}

type WithChild = { children: ReactElement }

export type DndDroppableOptions<P, D extends string | undefined> = undefined extends D
    ? DropOptionsBase<P, D> & LocalDropTarget<P, D>
    : DropOptionsBase<P, D> & KitDropTarget<P, D>

export type DndDroppableProps<P, D extends string | undefined> = undefined extends D
    ? DropOptionsBase<P, D> & LocalDropTarget<P, D> & WithChild
    : DropOptionsBase<P, D> & KitDropTarget<P, D> & WithChild

// Both branches of the conditional props are assignable to this shape, which
// is what lets one implementation serve both kit modes.
type AnyDroppableProps<P, D extends string | undefined> = DropOptionsBase<P, D> & {
    id?: Exclude<D, undefined>
    onDrop?: (event: DndDropEvent<P, D>) => void
}

export type DndDraggableOptions<P> = {
    /**
     * Item this element drags. `undefined` is not draggable: the child is left
     * untouched, same as `disabled`. A component whose item is optional can
     * always pass it here and to `useIsDragging`.
     */
    payload: P | undefined
    disabled?: boolean
}

export type DndZoneProps<P, D extends string | undefined> = {
    /** Overrides the kit canDrop inside this zone. Drops are still handled by the Droppable or the kit. */
    canDrop?: (args: DndCanDropArgs<P, D>) => boolean
    onTargetChange?: (change: DndTargetChange<P, D>) => void
    children: ReactNode
}

export type DndKit<P, D extends string | undefined> = {
    Draggable: (props: DndDraggableOptions<P> & WithChild) => ReactElement
    Droppable: (props: DndDroppableProps<P, D>) => ReactElement
    /**
     * Isolates its subtree: drags that start inside stay inside, and outside drags cannot enter.
     * Its canDrop sits between each Droppable's own and the kit's; the nearest defined one wins.
     */
    Zone: (props: DndZoneProps<P, D>) => ReactElement
    /** Prop builders for elements the components cannot wrap. They call no React hooks. */
    useDraggable: (args: DndDraggableOptions<P>) => DndElementProps
    useDroppable: (args: DndDroppableOptions<P, D>) => DndElementProps
    /**
     * True while this exact payload object is being dragged. A press still inside
     * the movement threshold is false. Comparison is `===`, so pass the same
     * object `Draggable` received. `undefined` is always false, matching a
     * `Draggable` whose payload is `undefined`. Put the result in `className`:
     * the engine copies that class onto the cursor clone. Inline style changes
     * are not copied.
     */
    useIsDragging: (item: P | undefined) => boolean
}

/** Event type of a kit's onDrop handlers, e.g. `DndDropEventOf<typeof practitionerDnd>`. */
export type DndDropEventOf<K> = K extends DndKit<infer P, infer D> ? DndDropEvent<P, D> : never

type KitOptions<P, D extends string | undefined> = {
    /** Debug name, used in component display names. */
    name?: string
    canDrop?: (args: DndCanDropArgs<P, D>) => boolean
    onDrop?: (event: DndDropEvent<P, D>) => void
}

export type DndKitOptionsWithDrop<P, DropId extends string> = KitOptions<P, DropId> & {
    onDrop: (event: DndDropEvent<P, DropId>) => void
}

export type DndKitOptionsWithoutDrop<P, DropId extends string> = KitOptions<P, DropId | undefined> & { onDrop?: undefined }

type KitHandlers<P, D extends string | undefined> = {
    canDrop?: (args: DndCanDropArgs<P, D>) => boolean
    onDrop?: (event: DndDropEvent<P, D>) => void
    onHoverChange?: (hover: DndHover<P> | null) => void
    onTargetChange?: (change: DndTargetChange<P, D>) => void
}

// The engine keeps payloads unknown so one engine serves every kit, and hands
// handlers events with the public fields. A kit's items are the only writers of
// payloads into its scope, so this cast is the typed boundary; callers of
// createDnd never see unknown. The cast only forgets P and D: returning the
// result as engine handlers is type-checked, so the event shapes cannot drift.
function engineHandlers<P, D extends string | undefined>(handlers: KitHandlers<P, D>): DndEngineHandlers {
    return handlers as KitHandlers<unknown, string | undefined>
}

function buildKit<P, D extends string | undefined>(options: KitOptions<P, D>): DndKit<P, D> {
    const name = options.name ?? 'DnD'
    const kitId = registerDndKit(engineHandlers({ canDrop: options.canDrop, onDrop: options.onDrop }))

    function dropHandlers(props: AnyDroppableProps<P, D>): DndEngineHandlers | undefined {
        // No own handlers: rely on the zone and kit layers and skip the ref entirely.
        if (props.canDrop === undefined && props.onDrop === undefined && props.onHoverChange === undefined) return undefined
        return engineHandlers(props)
    }

    function Draggable(props: DndDraggableOptions<P> & WithChild): ReactElement {
        const { payload, disabled, children } = props
        if (payload === undefined) return stampDndProps({ disabled: true, child: children })
        return stampDndProps({ drag: { kitId, payload }, disabled, child: children })
    }
    Draggable.displayName = `${name}.Draggable`

    function Droppable(props: AnyDroppableProps<P, D> & WithChild): ReactElement {
        const { id, disabled, children } = props
        return stampDndProps({ drop: { kitId, id, handlers: dropHandlers(props) }, disabled, child: children })
    }
    Droppable.displayName = `${name}.Droppable`

    function zoneHandlers(props: DndZoneProps<P, D>): DndEngineHandlers | undefined {
        const { canDrop, onTargetChange } = props
        // No handlers: the zone only isolates, so it needs no ref.
        if (canDrop === undefined && onTargetChange === undefined) return undefined
        return engineHandlers({ canDrop, onTargetChange })
    }

    function Zone(props: DndZoneProps<P, D>): ReactElement {
        const handlers = zoneHandlers(props)
        // display:contents keeps the wrapper out of layout; closest() still finds it.
        return (
            <div ref={handlers === undefined ? undefined : handlerRef(handlers)} {...{ [DND_ZONE_ATTR]: kitId }} style={{ display: 'contents' }}>
                {props.children}
            </div>
        )
    }
    Zone.displayName = `${name}.Zone`

    function useIsDragging(item: P | undefined): boolean {
        return useSyncExternalStore(
            subscribeDraggedPayload,
            () => item !== undefined && isDraggedPayload(kitId, item),
            () => false
        )
    }

    return {
        Draggable,
        Droppable,
        Zone,
        useDraggable: args => (args.payload === undefined ? {} : buildDraggableProps({ kitId, payload: args.payload, disabled: args.disabled })),
        useDroppable: (args: AnyDroppableProps<P, D>) => buildDroppableProps({ kitId, id: args.id, handlers: dropHandlers(args), disabled: args.disabled }),
        useIsDragging,
    }
}

/**
 * Creates a drag-and-drop kit for payload type `P`. The kit is the isolation
 * boundary: its draggables only reach its own droppables, with no provider.
 * Call once per kit, at module scope — each call registers a new kit.
 *
 * With a kit-level `onDrop`, every `Droppable` needs an `id` and may override
 * `onDrop`. Without one, each `Droppable` must handle its own drops and `id`
 * is optional. `canDrop` works the same way at both levels; the nearest wins.
 */
export function createDnd<P, DropId extends string = string>(options: DndKitOptionsWithDrop<P, DropId>): DndKit<P, DropId>
export function createDnd<P, DropId extends string = string>(options?: DndKitOptionsWithoutDrop<P, DropId>): DndKit<P, DropId | undefined>
export function createDnd<P, DropId extends string = string>(
    options?: DndKitOptionsWithDrop<P, DropId> | DndKitOptionsWithoutDrop<P, DropId>
): DndKit<P, DropId> | DndKit<P, DropId | undefined> {
    if (options?.onDrop !== undefined) return buildKit<P, DropId>(options)
    return buildKit<P, DropId | undefined>(options ?? {})
}
